import { Logger } from "../logger/logger";
import { BaseModel } from "../models/base-model";

export abstract class KoExtend {
	public static readonly subscribeBeforeAfter = <T>(observable: KnockoutObservable<T>, callback: (oldValue: T, newValue: T) => void) => {
		let value: any;
		observable.subscribe((oldValue) => { value = oldValue; }, null, "beforeChange");
		observable.subscribe((newValue) => { callback(value, newValue); });
	}

	public static readonly not = (observable: KnockoutObservable<any>): KnockoutComputed<boolean> => {
		let read = () => !observable();
		if (ko.isWriteableObservable(observable)) {
			let write = (newValue: any) => observable(!newValue);
			return ko.pureComputed({ read, write });
		} else {
			return ko.pureComputed(read);
		}
	}

	public static readonly waitForAsync = <T>(fn: () => T): Promise<T> => {
		let result = fn();
		if (result) {
			return Promise.resolve(result);
		}

		return new Promise((resolve) => {
			let computed = ko.pureComputed(fn);
			let subscription = computed.subscribe((newValue) => {
				if (newValue) {
					subscription.dispose();
					computed.dispose();
					resolve(newValue);
				}
			});
		});
	}

	public static readonly resolve = <T>(value: KnockoutObservable<T> | T): T => {
		if (value == null) {
			return value as T;
		}
		if (ko.isObservable(value)) {
			return value();
		} else {
			return value;
		}
	}

	public static subscribing<S extends KnockoutSubscribable<T>, T>(knockout: S, subscription: (newValue: T) => void): S {
		if (ko.isSubscribable(knockout)) {
			knockout.subscribe(subscription);
		} else {
			Logger.warn("Could not subscribing on knockout, since it is not subscribable");
		}

		return knockout;
	}

	public static deepClone<T>(sourceValue: T, keyName?: string): T {


		let ignoreKeys = ["isDirty", "dirtyFlag"];
		function throwError(message: string): never {
			message = `Error in deep clone: (Key ${keyName}) ` + message;
			Logger.error(message, sourceValue, typeof sourceValue);
			throw new Error(message + ": " + typeof sourceValue + ", value = " + sourceValue);
		}

		// TODO: handle more types
		switch (typeof sourceValue) {
			case "boolean":
			case "string":
			case "number":
			case "undefined":
				return sourceValue;

			case "function":
				throwError("Cannot clone functions, because they might capture `this` (or other variables) of the source value");
				break;

			case "object":
				if (sourceValue === null) {
					return sourceValue;
				}

				if (Array.isArray(sourceValue)) {
					return sourceValue.map((item) => KoExtend.deepClone(item)) as unknown as T;
				}

				if (sourceValue instanceof Date) {
					return new Date(sourceValue.getTime()) as unknown as T;
				}

				if (!(sourceValue instanceof BaseModel)) {
					//// TODO: handle more types (Date, etc..)
					//// TODO: deep clone generic objects?
					//throwError(`Unhandled object (Keys: ${Object.keys(value)})`);
					return $.extend(true, {}, ko.toJS(sourceValue));
				}

				let targetValue: any = new (sourceValue as any).constructor();

				for (let key in sourceValue) {
					if (ignoreKeys.includes(key)) {
						continue;
					}
					if (!sourceValue.hasOwnProperty(key)) {
						continue;
					}

					let item = (sourceValue as any)[key];

					if (ko.isObservable(item)) {
						if (!ko.isComputed(item) && ko.isWriteableObservable(item)) {

							let sourceItem = (sourceValue as any)[key]();
							let targetItem = KoExtend.deepClone(sourceItem, key);
							if (targetValue[key] == null) {
								throwError("targetValue[key] is null and expected knockout observable");
							}
							targetValue[key](targetItem);
						}
					} else if (typeof item === "function") {
						if (typeof targetValue[key] !== "function") {
							throwError("Missing function in destination");
						}
						continue; // don't clone functions, because they might capture `this` of the source value
					} else {

						let newValue = KoExtend.deepClone(item, key);
						targetValue[key] = newValue;
					}
				}

				return targetValue;
			default:
				throwError("Unhandled value type");
				break;
		}
	}
}